import type { AgingBasis, AgingReportData } from '@/types/AgingReport';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '@/services/api'; // Or standard axios instance

interface AgingReportState {
  report: AgingReportData | null;
  basis: AgingBasis;
  loading: boolean;
  error: string | null;
  asOfDate: string;
  warehouseId: number | null;
  currentRequestId: string | null;
}

const initialState: AgingReportState = {
  report: null,
  basis: 'due_date',
  loading: false,
  error: null,
  asOfDate: new Date().toISOString().slice(0, 10),
  warehouseId: null,
  currentRequestId: null,
};

export const fetchAgingReport = createAsyncThunk(
  'agingReport/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { agingReport?: AgingReportState };
      // Fall back to initialState: destructuring straight off a missing slice
      // threw a TypeError inside the thunk, which surfaced as a network error.
      const { asOfDate, warehouseId, basis } = state.agingReport ?? initialState;
      const params: Record<string, any> = { to: asOfDate, basis: basis ?? 'due_date' };
      if (warehouseId !== null) {
        params.warehouse_id = warehouseId;
      }
      
      const response = await api.get('/financial-statements/aging-report', { params });
      return response.data.data ?? response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? err?.message ?? 'Failed to load aging report');
    }
  }
);

export const agingReportSlice = createSlice({
  name: 'agingReport',
  initialState,
  reducers: {
    setAgingBasis(state, action: PayloadAction<AgingBasis>) {
      state.basis = action.payload;
      state.currentRequestId = null;
      state.report = null;
    },
    setAsOfDate(state, action: PayloadAction<string>) {
      state.asOfDate = action.payload;
      state.currentRequestId = null;
      state.report = null;
    },
    setWarehouseId(state, action: PayloadAction<number | null>) {
      state.warehouseId = action.payload;
      state.currentRequestId = null;
      state.report = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgingReport.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAgingReport.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.report = action.payload;
        state.currentRequestId = null;
      })
      .addCase(fetchAgingReport.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.currentRequestId = null;
        // Drop the previous report: it was computed for the PREVIOUS asOfDate /
        // warehouseId, so keeping it next to the new filter values would show
        // numbers that do not match what the screen is asking about.
        state.report = null;
        state.error = typeof action.payload === 'string'
          ? action.payload
          : 'Failed to load aging report';
      });
  },
});

export const { setAsOfDate, setWarehouseId, setAgingBasis } = agingReportSlice.actions;

export default agingReportSlice.reducer;
